/* global globalThis */
import { useState, useCallback } from "react"
import { useDispatch, useSelector } from "react-redux"
import { toaster } from "components/common/Toast"
import { createBoat, updateBoat } from "store/boats"
import { validateMissingAsset, findDuplicateAssetNumber } from "../utils/validation"
import {
  buildPropertiesAttributes,
  convertMediaForDuplication,
  appendExperiences,
  appendCharacteristics,
  appendMedia,
  appendAssets
} from "../utils/payloadBuilder"

/**
 * Custom hook for handling equipment form submission
 * Includes validation, FormData building, and API calls
 *
 * @param {Object} formHook - Hook object from useEquipmentForm
 * @param {Object} characteristicsHook - Hook object from useEquipmentCharacteristics
 * @param {Object} record - Equipment record (for updates)
 * @param {boolean} isDuplicateMode - Whether in duplicate mode
 * @param {Function} onCancel - Callback to close modal after success
 * @returns {Object} - Submit state and handler
 */
export const useEquipmentSubmit = (formHook, characteristicsHook, record, isDuplicateMode, onCancel) => {
  const [isLoading, setIsLoading] = useState(false)
  const dispatch = useDispatch()
  const characteristicListing = useSelector((state) => state.boats.characteristic_to_boats.items) || []

  const handleSubmit = useCallback(
    async (values) => {
      setIsLoading(true)
      // Filter out placeholder assets before validation
      const realAssets = formHook.assets.filter((a) => !a.isPlaceholder)

      // Validate at least one real asset exists
      const activeAssets = realAssets.filter((a) => !(a?._destroy || a?.destroy))
      if (activeAssets.length === 0) {
        setIsLoading(false)
        toaster.error("Please add at least one asset by clicking 'Add New Item'")
        return
      }

      // Validate blank asset numbers (non-destroyed assets)
      const missingAsset = validateMissingAsset(realAssets)
      if (missingAsset) {
        setIsLoading(false)
        toaster.error("Please fill asset number")
        return
      }

      // Validate duplicate asset numbers among active (not marked for destroy) assets
      const duplicate = findDuplicateAssetNumber(realAssets)
      if (duplicate) {
        if (globalThis.window !== undefined) {
          toaster.error(`Duplicate asset number: ${duplicate}`)
          setIsLoading(false)
        }
        return
      }

      // Convert URL-based media to File objects in duplicate mode
      let mediaToSubmit = formHook.localMedia
      if (isDuplicateMode) {
        try {
          mediaToSubmit = await convertMediaForDuplication(formHook.localMedia)
        } catch (error) {
          console.error("Failed to convert media:", error)
          setIsLoading(false)
          toaster.error("Failed to process media files. Please try again.")
          return
        }
      }

      // Build properties attributes
      const properties_attributes = buildPropertiesAttributes(mediaToSubmit)

      // Build FormData payload
      const formData = new FormData()
      formData.append("boat[name]", values.name)
      formData.append("boat[description]", values.description)
      formData.append("boat[max_guests]", values.max_users || 1)
      formData.append("boat[boat_category_id]", values.category || "")

      appendExperiences(formData, formHook.selectedExperiences)
      appendCharacteristics(
        formData,
        characteristicsHook.selected,
        characteristicListing,
        characteristicsHook.removedCharacteristics,
        characteristicsHook.userCreatedCharacteristics
      )
      appendMedia(formData, properties_attributes)

      // Filter out assets already deleted via API (marked with _destroy)
      // They were deleted separately, so don't send them again in the update payload
      const assetsToSend = realAssets.filter((a) => !a._destroy)
      appendAssets(formData, assetsToSend)

      // Submit
      // In duplicate mode, always create new equipment (ignore record.id)
      const isUpdate = record?.id && !isDuplicateMode
      const successMsg = isUpdate ? "Equipment updated successfully" : "Equipment created successfully"
      const submitPromise = isUpdate ? dispatch(updateBoat(record.id, formData)) : dispatch(createBoat(formData))

      submitPromise
        .then(() => {
          toaster.success(successMsg)
          onCancel()
          setIsLoading(false)
        })
        .catch(() => {
          setIsLoading(false)
        })
    },
    [
      formHook.assets,
      formHook.localMedia,
      formHook.selectedExperiences,
      characteristicsHook.selected,
      characteristicsHook.removedCharacteristics,
      characteristicsHook.userCreatedCharacteristics,
      characteristicListing,
      record,
      isDuplicateMode,
      onCancel,
      dispatch
    ]
  )

  return {
    isLoading,
    handleSubmit
  }
}
